import { RouterRootState } from 'connected-react-router';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { toast } from "react-toastify";
import Description from '../../../components/description/Description';
import { useDialog } from '../../../components/dialog/DialogContext';
import { Topic } from '../../../components/item-content-two/Topic';
import ItemContentTabTwo from '../../../components/item-content-two/item-content-tab_two';
import SimpleItemDetails from '../../../components/item-content/SimpleItemDetails';
import { PresetCard } from '../../../components/preset/PresetCard';
import PresetActions from '../../../components/preset/presetActions';
import PresetsDeviceSchedules from '../../../components/preset/presetDeviceSchedules';
import PresetInputs from '../../../components/preset/presetInputs';
import PresetLocks from '../../../components/preset/presetLocks';
import PresetOutputs from '../../../components/preset/presetOutputs';
import PresetParameters from '../../../components/preset/presetParameters';
import { SystemProfileTypeIds } from '../../../components/preset/presetSettingsWizard';
import { SelectType } from '../../../components/settingFields/SettingSelectField';
import { PRESET_TYPES } from '../../../constants/preset-constants';
import { TranslationKeys } from '../../../contexts/TranslationKeys';
import { useLocale } from '../../../contexts/localeContext';
import { TopicContextProvider } from '../../../contexts/topic/TopicContextProvider';
import { BreadcrumbLink } from '../../../models/BreadcrumbLink';
import { ContentWrapper } from '../../../models/ContentWrapper';
import { DevicePreset as LockPresetModel } from '../../../models/DevicePreset';
import { MultiSelectOption } from '../../../models/MultiSelectOption';
import { TabSummary } from '../../../models/TabSummary';
import { DeleteEntityResult } from '../../../models/services/DeleteEntityResult';
import { getDeviceProfileNames, getLockPresetsById, storeLockPreset } from '../../../services/hardwarePresetService';
import { deletePresetSetting, getReaderPresetMobileSettings } from '../../../services/presetService';
import { getErrorMessage, showLoadItemError } from '../../../services/utilities/ServiceError';
import { IAppState } from '../../../store/types';
import PresetMobile from '../../../components/preset/presetMobile';
import { saveSettingData } from '../../../services/settingDataService';
import { getSettingDataToEdit } from '../../../utilities/presetUtil';
import { useGlobalState } from '../../../Providers';
import { SPECIFIC_HELP_KEY } from '../../../constants/specific-help-constant';

const getIdFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}

const initialItem : LockPresetModel = {
    id: 0,
    name: '',
    type: '',
    devices: 0,
    site: '',
    siteId: 0,
    profileTypeId: '',
    pInheritsFrom: '',
    inheritsFromId: 0,
    description: '',
    totalDevices: 0,
    totalActions: 0,
    totalParameters: 0,
    totalInputs: 0,
    totalDoorModePatterns: 0,
    totalOutputs: 0
}

const allItemsPath = '/access-control/hardware/presets';
const itemPath = '/access-control/hardware/lock-preset/';

const LockPreset: FC = () => {
    const { translate } = useLocale();
    const initialTabs = [
        { label: translate.byKey(TranslationKeys.mobile), badge: 0, showInitial: true, isActive: false },
        { label: translate.byKey(TranslationKeys.inputs), badge: 0, showInitial: true, isActive: false },
        { label: translate.byKey(TranslationKeys.outputs), badge: 0, showInitial: true, isActive: false },
        { label: translate.byKey(TranslationKeys.actions), badge: 0, showInitial: true, isActive: false },
        { label: translate.byKey(TranslationKeys.parameters), badge: 0, showInitial: true, isActive: false },
        { label: translate.byKey(TranslationKeys.schedules), badge: 0, showInitial: true, isActive: false },
        { label: translate.byKey(TranslationKeys.locks), badge: 0, showInitial: true, isActive: false },
        { label: translate.byKey(TranslationKeys.notes), badge: 0, showInitial: true, isActive: true }
    ];
    const siteId = useSelector((state: IAppState) => state.site.site.value);
    const history = useHistory();
    const pageId = useSelector((state: RouterRootState) => getIdFromPath(state.router.location.pathname));
    const [itemId, setItemId] = useState(pageId);
    const [currentItem, setCurrentItem] = useState<LockPresetModel>(initialItem);
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbLink[]>([]);
    const [busy, setBusy] = useState(false);
    const [tabs, setTabs] = useState<TabSummary[]>([]);
    const [selectedItem, setSelectedItem] = useState<MultiSelectOption | undefined>(undefined);
    const dialog = useDialog();
    const [originalMobileValues, setOriginalMobileValues] = useState<any>();
    const {setSpecificHelpTopicKey} = useGlobalState() ?? { setSpecificHelpTopicKey: undefined };

    const onDelete = (id: string): Promise<DeleteEntityResult> => {
        setBusy(true);
        return new Promise((resolve, reject) => {
            deletePresetSetting(id)
                .then(
                    deleteResult => {
                        resolve(deleteResult);
                        reloadPage();
                        setBusy(false);
                    },
                    deleteResult => {
                        reject(deleteResult);
                        setBusy(false);
                    })
        });
    }

    const onPresetSave = (createNew: boolean) => {
        if (createNew) {
            reloadPage();
        }
    }

    const onPresetDeleteUpdateBadge = (index) => {
        setTabs(prevTabs => updateTabBadgeTotal(index, prevTabs[index].badge - 1, prevTabs));
    }

    const topic: Topic = {
        accessor: 'nameOriginal',
        title: translate.byKey(TranslationKeys.lockPresets),
        allItemsPath: allItemsPath,
        itemPath: itemPath
    }

    const initialTabContent: ContentWrapper[] = [
        {
            content: <PresetMobile />
        },
        {
            content: <PresetInputs onDelete={onDelete} currentItemDetails={currentItem} onSave={onPresetSave} profileId={SystemProfileTypeIds.Reader} itemId={itemId} />,
        },
        {
            content: <PresetOutputs itemId={itemId} />,
        },
        {
            content: <PresetActions onDelete={onDelete} currentItemDetails={currentItem} onSave={onPresetSave} profileId={SystemProfileTypeIds.Reader} itemId={itemId} />,

        },
        {
            content: <PresetParameters onSave={onPresetSave} profileId={SystemProfileTypeIds.Reader} itemId={itemId} />,
        },
        {
            content: <PresetsDeviceSchedules itemId={itemId} onDelete={onDelete} onSave={onPresetSave} profileType={SystemProfileTypeIds.Reader} onDeleteUpdateBadge={(createNew) => { if (createNew) onPresetDeleteUpdateBadge(4) }} />,
        },
        {
            content: <PresetLocks itemId={itemId} />,
        },
        {
            content: <Description title={translate.byKey(TranslationKeys.notes)} />
        }
    ];
    const [tabContentList, setTabContentList] = useState<ContentWrapper[]>(initialTabContent);

    const reloadPage = async () => {
        try {
            setTabs([])
            let lockPreset;

            if (itemId) {
                lockPreset = await getLockPresetsById(itemId);
                const mobileValues = await getReaderPresetMobileSettings(lockPreset.id);
                setOriginalMobileValues(mobileValues);
                setSelectedItem({label: lockPreset.name, value: lockPreset.id.toString(), isDefault: lockPreset.isDefault});
                setCurrentItem({ ...lockPreset, mobileValues });
                setSpecificHelpTopicKey && setSpecificHelpTopicKey(SPECIFIC_HELP_KEY.LOCK_PRESET);
                const doesPresetSupportMobile = mobileValues.settingDatasRefs && mobileValues.settingDatasRefs.length > 0;
                const newTabs = [...initialTabs];
                const newTabsContent = [...initialTabContent];
                let tabIndex = 1;

                if (!doesPresetSupportMobile) {
                    newTabs.shift();
                    newTabsContent.shift();
                    tabIndex = 0;
                }
                else {
                    newTabs[0].isActive = true;
                }

                const updatedTabs = updateTabBadgeTotal(tabIndex, lockPreset?.totalInputs,
                    updateTabBadgeTotal(tabIndex += 1, 0,
                        updateTabBadgeTotal(tabIndex += 1, lockPreset?.totalActions,
                            updateTabBadgeTotal(tabIndex += 1, lockPreset?.totalParameters,
                                updateTabBadgeTotal(tabIndex += 1, lockPreset?.totalDoorModePatterns,
                                    updateTabBadgeTotal(tabIndex += 1, lockPreset?.totalDevices, newTabs))))));

                setTabs(updatedTabs);
                setTabContentList(newTabsContent);
            }
            else {
                setCurrentItem(initialItem);
                setTabs([initialTabs[7]]);
            }

            setBreadcrumb([
                { value: '/access-control/hardware', displayName: translate.byKey(TranslationKeys.hardware) },
                { value: allItemsPath, displayName: translate.byKey(TranslationKeys.preset) },
                { value: itemPath + `${itemId}`, displayName: `${lockPreset?.name ?? ''}` }
            ]);
        } catch (error) {
            showLoadItemError(error, dialog, translate, () => { history.push(allItemsPath) });
        }
    }

    useEffect(() => {
        setTabs([]);
        setItemId(pageId);
    }, [pageId])

    useEffect(() => {
        reloadPage();
    }, [itemId])

    const getTabContent = (index?: number) => {
        const notesIndex = tabContentList.length - 1;
        const selectedTabContent = itemId ? tabContentList[index ?? 0] : tabContentList[notesIndex];
        return selectedTabContent.content;
    };

    const updateTabBadgeTotal = (tabIndex, totalCount, currentTabs) => {
        const newTabs = [...currentTabs];
        newTabs[tabIndex].badge = totalCount;
        newTabs[tabIndex].isActive = true; // Set tab to active so we can show it
        return newTabs;
    };

    const saveLockPreset = async (values) => {
        try {
            setBusy(true);
            const newLockPreset: LockPresetModel = {
                ...currentItem,
                name: values.name,
                id: values.id,
                pInheritsFrom: values.pInheritsFrom,
                inheritsFromId: values.inheritsFromId,
                siteId: currentItem?.siteId ? currentItem?.siteId : siteId,
                description: values.description
            }

            const isNew = newLockPreset.id ? false : true;

            if (values.name !== currentItem.name || values.description !== currentItem.description) {
                const response: any = await storeLockPreset(newLockPreset, isNew);
                history.push(`${itemPath}${response.id}`);
                setItemId(response.id)
            }

            // Edit mobile settings can only be triggered for an existing preset
            if (!isNew) {
                await editMobileSettings(values);
            }
            toast.success(translate.byKey(TranslationKeys.lockPresetSaved));
        }
        catch (error) {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
        }
        finally {
            setBusy(false);
        }
    }

    const editMobileSettings = async (values) => {
        try {
            const allPromises = await Promise.all(getSettingDataToEdit(values, originalMobileValues).map(saveSettingData));
            const allFailed = allPromises.filter((promise) => !promise.success);
            if (allFailed.length > 0) {
               toast.warn(translate.byKey(TranslationKeys.error));
            }
        } catch (error) {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
        } finally {
            reloadPage();
        }
    }

    return <div className="flex h-full">
         <TopicContextProvider dataMethod={(listparameters) => getDeviceProfileNames(PRESET_TYPES.LOCK, listparameters)} itemId={itemId} topic={topic} selectedItem={selectedItem}>
            <ItemContentTabTwo>
                <SimpleItemDetails
                    breadcrumb={breadcrumb}
                    busy={busy}
                    getTabContent={getTabContent}
                    itemProfile={currentItem}
                    onBack={() => history.push(allItemsPath)}
                    saveDetails={saveLockPreset}
                    tabs={tabs}
                    itemPath={itemPath}
                    initialValues={currentItem}>
                    <PresetCard presetType={SelectType.lockProfile}></PresetCard>
                </SimpleItemDetails>
            </ItemContentTabTwo>
        </TopicContextProvider>
    </div>
}

export default LockPreset
